/** Default options for short numeric dates in tables and filters. */
export const INTL_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric'
}

/** Default options for short times in table columns. */
export const INTL_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit'
}

/** Default options for combined date+time in table columns. */
export const INTL_DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...INTL_DATE_OPTIONS,
  ...INTL_TIME_OPTIONS
}

/**
 * Parse an ISO string or Date; return null when missing or invalid.
 *
 * :param value: ISO datetime string or Date instance
 * :return: Valid Date or null
 */
export const parseToDate = (value: string | Date | null | undefined): Date | null => {
  if (value == null || value === '') {
    return null
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Format a date for display using a BCP 47 locale tag.
 *
 * :param value: ISO datetime string or Date
 * :param intlLocale: BCP 47 tag (e.g. `de-DE`, `fr-FR`)
 * :param options: Optional Intl.DateTimeFormat overrides
 * :return: Localized date string, or empty string when invalid
 */
export const formatIntlDate = (
  value: string | Date | null | undefined,
  intlLocale: string,
  options: Intl.DateTimeFormatOptions = INTL_DATE_OPTIONS
): string => {
  const date = parseToDate(value)
  if (!date) {
    return ''
  }
  return date.toLocaleDateString(intlLocale, options)
}

/**
 * Format date and time for display using a BCP 47 locale tag.
 *
 * :param value: ISO datetime string or Date
 * :param intlLocale: BCP 47 tag (e.g. `de-DE`, `fr-FR`)
 * :param withTime: When false, only the date portion is returned
 * :param options: Optional Intl.DateTimeFormat overrides (ignored when withTime is false)
 * :return: Localized date/time string, or empty string when invalid
 */
export const formatIntlDateTime = (
  value: string | Date | null | undefined,
  intlLocale: string,
  withTime = true,
  options: Intl.DateTimeFormatOptions = INTL_DATETIME_OPTIONS
): string => {
  const date = parseToDate(value)
  if (!date) {
    return ''
  }
  if (!withTime) {
    return date.toLocaleDateString(intlLocale, INTL_DATE_OPTIONS)
  }
  return date.toLocaleString(intlLocale, options)
}

/**
 * Split a timestamp into localized date and time parts for stacked table cells.
 *
 * :param value: ISO datetime string or Date
 * :param intlLocale: BCP 47 tag (e.g. `de-DE`, `fr-FR`)
 * :return: Object with datePart and timePart (empty strings when invalid)
 */
export const formatIntlDateParts = (
  value: string | Date | null | undefined,
  intlLocale: string
): { datePart: string; timePart: string } => {
  const date = parseToDate(value)
  if (!date) {
    return { datePart: '', timePart: '' }
  }
  return {
    datePart: date.toLocaleDateString(intlLocale, INTL_DATE_OPTIONS),
    timePart: date.toLocaleTimeString(intlLocale, INTL_TIME_OPTIONS)
  }
}

/**
 * Format a calendar picker day for range-filter labels.
 *
 * :param date: Year/month/day from @internationalized/date, or undefined
 * :param intlLocale: BCP 47 tag (e.g. `de-DE`, `fr-FR`)
 * :return: Localized date string, or empty string when date is missing
 */
export const formatCalendarDateForDisplay = (
  date: { year: number; month: number; day: number } | undefined,
  intlLocale: string
): string => {
  if (!date) {
    return ''
  }
  const jsDate = new Date(date.year, date.month - 1, date.day)
  return formatIntlDate(jsDate, intlLocale)
}
